import type { ReactNode } from 'react';
import { Button2, Menu, MenuRadio, MenuRadioGroup } from '@frameio/vapor';
import { IconChevronDown } from '@frameio/vapor-icons';
import { SettingRow } from './SettingRow';

type SettingRowSelectProps = {
  /** `data-testid` applied to the trigger button so e2e tests can target the select. */
  dataTestId?: string;
  /** Accessible label for the trigger and the options menu. */
  name: string;
  /** Called with the selected value. */
  onChange: (value: string) => void;
  /** Select options as `{ value, label }` pairs. */
  options: { value: string; label: string }[];
  /** Row title (label shown next to the select trigger). */
  title: ReactNode;
  /** Currently selected value. */
  value: string;
};

export const SettingRowSelect = ({
  dataTestId,
  name,
  onChange,
  options,
  title,
  value,
}: SettingRowSelectProps) => {
  const selectedLabel = options.find((o) => o.value === value)?.label ?? value;

  return (
    <SettingRow
      field={
        <Menu
          aria-label={name}
          content={
            <MenuRadioGroup
              checked={value}
              maxHeight="200px"
              onChange={onChange}
            >
              {options.map((option) => (
                <MenuRadio key={option.value} name={option.value}>
                  {option.label}
                </MenuRadio>
              ))}
            </MenuRadioGroup>
          }
          placement="bottom-end"
        >
          <Button2
            aria-label={`${name}: ${selectedLabel}`}
            block={true}
            data-testid={dataTestId}
            iconAfter={<IconChevronDown />}
            spaceBetween={true}
          >
            {selectedLabel}
          </Button2>
        </Menu>
      }
      title={title}
    />
  );
};
